import { create } from 'zustand';
import { newId } from '@/utils/ids';
import type { SyncError, SyncOperation } from './syncTypes';

type SyncPhase = 'push' | 'pull';
type SyncLogStatus = 'success' | 'error' | 'conflict';

export type SyncLogEntry = {
  id: string;
  timestamp: string;
  phase: SyncPhase;
  status: SyncLogStatus;
  message: string;
  table?: string;
  recordId?: string;
  operation?: SyncOperation;
};

type SyncStatusState = {
  syncing: boolean;
  lastSyncAt: string | null;
  lastError: SyncError | null;
  lastConflict: SyncError | null;
  log: SyncLogEntry[];
  setSyncing: (value: boolean) => void;
  recordLog: (entry: Omit<SyncLogEntry, 'id' | 'timestamp'>) => void;
  recordSuccess: (phase: SyncPhase, message?: string) => void;
  recordError: (error: SyncError) => void;
  recordConflict: (error: SyncError) => void;
  clearLastError: () => void;
  clearLastConflict: () => void;
};

const MAX_LOG_ENTRIES = 100;

function appendLog(entries: SyncLogEntry[], entry: SyncLogEntry): SyncLogEntry[] {
  return [entry, ...entries].slice(0, MAX_LOG_ENTRIES);
}

export const useSyncStatusStore = create<SyncStatusState>((set) => ({
  syncing: false,
  lastSyncAt: null,
  lastError: null,
  lastConflict: null,
  log: [],
  setSyncing: (value) => set({ syncing: value }),
  recordLog: (entry) =>
    set((state) => ({
      log: appendLog(state.log, {
        id: newId(),
        timestamp: new Date().toISOString(),
        ...entry,
      }),
    })),
  recordSuccess: (phase, message) =>
    set((state) => ({
      syncing: false,
      lastSyncAt: new Date().toISOString(),
      log: appendLog(state.log, {
        id: newId(),
        timestamp: new Date().toISOString(),
        phase,
        status: 'success',
        message: message ?? 'Sync completata',
      }),
    })),
  recordError: (error) =>
    set((state) => ({
      syncing: false,
      lastError: error,
      log: appendLog(state.log, {
        id: newId(),
        timestamp: new Date().toISOString(),
        phase: error.phase,
        status: 'error',
        message: error.message,
        table: error.table,
        recordId: error.recordId,
        operation: error.operation,
      }),
    })),
  recordConflict: (error) =>
    set((state) => ({
      lastConflict: error,
      log: appendLog(state.log, {
        id: newId(),
        timestamp: new Date().toISOString(),
        phase: error.phase,
        status: 'conflict',
        message: error.message,
        table: error.table,
        recordId: error.recordId,
        operation: error.operation,
      }),
    })),
  clearLastError: () => set({ lastError: null }),
  clearLastConflict: () => set({ lastConflict: null }),
}));

export function markSyncStart(): void {
  useSyncStatusStore.getState().setSyncing(true);
}

export function markSyncEnd(): void {
  useSyncStatusStore.getState().setSyncing(false);
}

export function recordSyncSuccess(phase: SyncPhase, message?: string): void {
  useSyncStatusStore.getState().recordSuccess(phase, message);
}

export function recordSyncLog(entry: Omit<SyncLogEntry, 'id' | 'timestamp'>): void {
  useSyncStatusStore.getState().recordLog(entry);
}

export function recordSyncError(error: SyncError): void {
  useSyncStatusStore.getState().recordError(error);
}

export function recordSyncConflict(error: SyncError): void {
  useSyncStatusStore.getState().recordConflict(error);
}
